
import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import InviteSignup from "@/pages/InviteSignup";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { Skeleton } from "@/components/ui/skeleton";
import { VendorRedirectInterceptor } from "@/components/vendor/VendorRedirectInterceptor";
import { createDynamicImport, preloadCalendarComponents, preloadChartComponents, createErrorBoundaryImport } from "@/utils/importUtils";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { isLovableEditor, useSafeRendering } from "@/utils/environment";

// Eagerly loaded components for critical paths
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";
import ServiceDetails from "@/pages/ServiceDetails";
import NotFound from "@/pages/NotFound";

// Directly imported components (to avoid lazy loading issues)
import MyProfile from "@/pages/MyProfile";
import Favorites from "@/pages/Favorites";
import ServiceDashboard from "@/pages/ServiceDashboard";
import VendorSettings from "@/pages/VendorSettings";
import ServiceForm from "@/pages/ServiceForm";
import TermsOfService from "@/pages/TermsOfService";
import AdminServices from "@/pages/AdminServices";
import AdminDashboard from "@/pages/AdminDashboard";
import ManageUsers from "@/pages/ManageUsers";
import ManageCategories from "@/pages/ManageCategories";
import ManageRegions from "@/pages/ManageRegions";
import ManageBookings from "@/pages/ManageBookings";
import ManageReviews from "@/pages/ManageReviews";
import CompletedBookings from "@/pages/CompletedBookings";
import { BookingDetailsPage } from "@/pages/BookingDetailsPage";
import DebugReviews from "@/pages/DebugReviews";
import AdminSettings from "@/pages/AdminSettings";
import AdminTerms from "@/pages/AdminTerms";
import AdminTesting from "@/pages/AdminTesting";
import SetupProfile from "@/pages/SetupProfile";
import MyBookings from "@/pages/MyBookings";
import MyReviews from "@/pages/MyReviews";
import AboutUs from "@/pages/AboutUs";
import AdminAboutUs from "@/pages/AdminAboutUs";
import AdminBrand from "@/pages/AdminBrand";
import Verify from "@/pages/Verify";
import UserDetails from "@/pages/UserDetails";
import VendorHelp from "@/pages/VendorHelp";
import { PublicBookingView } from "@/components/booking/PublicBookingView";

// Lazy load AdminMedia to prevent edge function calls on initial load
const AdminMedia = lazy(() => import("@/pages/AdminMedia"));

// Conditionally import based on environment
const shouldLazyLoad = !isLovableEditor() && !useSafeRendering();

// Lazily loaded components with enhanced import handling (only for non-critical paths)
const Dashboard = shouldLazyLoad
  ? lazy(createDynamicImport("@/pages/Dashboard"))
  : MyProfile; // Fallback for Lovable editor

const MagicLink = shouldLazyLoad
  ? lazy(createDynamicImport("@/pages/MagicLink"))
  : Auth; // Fallback for Lovable editor

const TestCalendar = shouldLazyLoad
  ? lazy(createDynamicImport("@/pages/TestCalendar"))
  : ServiceDashboard; // Fallback for Lovable editor

// Simple fallback for suspense
const SimpleFallback = () => (
  <div className="min-h-screen bg-gray-50 pt-16">
    <div className="container py-8">
      <Skeleton className="h-12 w-1/3 mb-6" />
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  </div>
);

// Lovable editor specialized suspense fallback
const LovableEditorFallback = () => (
  <div className="p-4 border border-blue-100 bg-blue-50 rounded-md m-4">
    <p className="text-blue-700 font-medium">Component Loading</p>
    <p className="text-sm text-blue-600">This component is loading in the Lovable editor...</p>
  </div>
);

export const AppRoutes = () => {
  // Choose appropriate fallback based on environment
  const FallbackComponent = isLovableEditor() ? LovableEditorFallback : SimpleFallback;
  
  // Preload components based on current route
  useEffect(() => {
    // Only preload if not in Lovable editor to avoid unnecessary network requests
    if (!isLovableEditor()) {
      const currentPath = window.location.pathname;
      
      // Only preload calendar components for relevant routes
      preloadCalendarComponents(currentPath);
      
      // Preload chart components for admin pages
      if (currentPath.startsWith('/admin')) {
        preloadChartComponents();
      }
    }
  }, []);
  
  return (
    <Routes>
      {/* Critical path routes - eagerly loaded with vendor redirect interceptor */}
      <Route path="/" element={
        <VendorRedirectInterceptor>
          <Index />
        </VendorRedirectInterceptor>
      } />
      <Route path="/auth/*" element={<Auth />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/invite" element={
        <div className="min-h-screen bg-background">
          <InviteSignup />
        </div>
      } />
      
      {/* Email verification page */}
      <Route path="/verify" element={<Verify />} />
      
      {/* Vendor Help page */}
      <Route path="/vendor/help" element={
        <AuthGuard requireVendor={true}>
          <VendorHelp />
        </AuthGuard>
      } />
      
      {/* About Us page */}
      <Route path="/about-us" element={<AboutUs />} />
      
      {/* Public booking view - no auth required */}
      <Route path="/booking/view/:token" element={
        <ErrorBoundary componentName="PublicBookingView">
          <PublicBookingView />
        </ErrorBoundary>
      } />
      
      {/* Service Details route with calendar preloading */}
      <Route path="/service/:id" element={
        <ErrorBoundary componentName="ServiceDetails">
          <ServiceDetails />
        </ErrorBoundary>
      } />
      
      {/* Non-critical routes - directly imported */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      
      {/* Only lazy load the Dashboard */}
      <Route path="/dashboard" element={
        <AuthGuard>
          <Suspense fallback={<FallbackComponent />}>
            <Dashboard />
          </Suspense>
        </AuthGuard>
      } />
      
      {/* Profile pages - direct imports */}
      <Route path="/my-profile" element={
        <AuthGuard>
          <MyProfile />
        </AuthGuard>
      } />
      
      <Route path="/profile" element={<Navigate to="/my-profile" replace />} />
      <Route path="/setup-profile" element={
        <AuthGuard>
          <SetupProfile />
        </AuthGuard>
      } />
      <Route path="/my-bookings" element={
        <AuthGuard>
          <MyBookings />
        </AuthGuard>
      } />
      <Route path="/booking/:serviceId" element={
        <AuthGuard>
          <MyBookings />
        </AuthGuard>
      } />
      <Route path="/booking/:bookingId/details" element={
        <AuthGuard>
          <ErrorBoundary componentName="BookingDetailsPage">
            <BookingDetailsPage />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/my-reviews" element={
        <AuthGuard>
          <MyReviews />
        </AuthGuard>
      } />
      
      {/* Favorites with direct import */}
      <Route path="/favorites" element={
        <AuthGuard>
          <Favorites />
        </AuthGuard>
      } />
      
      {/* Service Dashboard Routes - direct import */}
      <Route path="/service/:id/calendar" element={
        <AuthGuard>
          <ErrorBoundary componentName="ServiceDashboard">
            <ServiceDashboard />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/service/:id/bookings" element={
        <AuthGuard>
          <ErrorBoundary componentName="ServiceDashboard">
            <ServiceDashboard />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/service/:id/reviews" element={
        <AuthGuard>
          <ErrorBoundary componentName="ServiceDashboard">
            <ServiceDashboard />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/service/:id/edit" element={
        <AuthGuard>
          <ErrorBoundary componentName="ServiceDashboard">
            <ServiceDashboard />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/service/:id/dashboard" element={
        <AuthGuard>
          <ErrorBoundary componentName="ServiceDashboard">
            <ServiceDashboard />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/service/:id/settings" element={
        <AuthGuard>
          <ErrorBoundary componentName="VendorSettings">
            <VendorSettings />
          </ErrorBoundary>
        </AuthGuard>
      } />
      
      {/* Admin Routes - direct imports */}
      <Route path="/admin" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminDashboard">
            <AdminDashboard />
          </ErrorBoundary>
        </AuthGuard>
      } />
      
      <Route path="/admin/services" element={
        <AuthGuard adminOnly>
          <AdminServices />
        </AuthGuard>
      } />
      
      <Route path="/admin/services/new" element={
        <AuthGuard adminOnly>
          <ServiceForm />
        </AuthGuard>
      } />
      <Route path="/admin/services/edit/:id" element={
        <AuthGuard>
          <ServiceForm />
        </AuthGuard>
      } />
      
      <Route path="/admin/users" element={
        <AuthGuard adminOnly>
          <ManageUsers />
        </AuthGuard>
      } />
      
      <Route path="/admin/users/edit/:userId" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="UserDetails">
            <UserDetails />
          </ErrorBoundary>
        </AuthGuard>
      } />
      
      <Route path="/admin/categories" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminCategories">
            <ManageCategories />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/regions" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminRegions">
            <ManageRegions />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/bookings" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminBookings">
            <ManageBookings />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/completed" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="CompletedBookings">
            <CompletedBookings />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/reviews" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminReviews">
            <ManageReviews />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/reviews-debug" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="DebugReviews">
            <DebugReviews />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/about-us" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminAboutUs">
            <AdminAboutUs />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/brand" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminBrand">
            <AdminBrand />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/settings" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminSettings">
            <AdminSettings />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/terms" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminTerms">
            <AdminTerms />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/testing" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminTesting">
            <AdminTesting />
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/media" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminMedia">
            <Suspense fallback={<FallbackComponent />}>
              <AdminMedia />
            </Suspense>
          </ErrorBoundary>
        </AuthGuard>
      } />
      <Route path="/admin/*" element={
        <AuthGuard adminOnly>
          <ErrorBoundary componentName="AdminDashboard">
            <AdminDashboard />
          </ErrorBoundary>
        </AuthGuard>
      } />
      
      {/* Testing Routes - keep these lazy loaded */}
      <Route path="/test/calendar" element={
        <Suspense fallback={<FallbackComponent />}>
          <TestCalendar />
        </Suspense>
      } />
      <Route path="/magic-link" element={
        <Suspense fallback={<FallbackComponent />}>
          <MagicLink />
        </Suspense>
      } />
      
      {/* Catch-all route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
